import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

export default function CategoryDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <section className="w-full bg-background-50 py-10 md:py-14">
      <div className="w-full max-w-6xl mx-auto px-4 md:px-6">
        <div className="text-center mb-8">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-3">
            Browse Vigorbuy Spreadsheet Categories
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
            Select a category below to explore products organized in our comprehensive spreadsheets
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-10">
          {categories.map((cat, index) => (
            <a
              key={index}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="group flex flex-col items-center gap-2 p-3 rounded-lg bg-background-100 border border-background-200 hover:border-primary-200 hover:bg-primary-50/50 transition-all duration-200 cursor-pointer"
            >
              <div className="w-14 h-14 md:w-16 md:h-16 rounded-lg overflow-hidden flex-shrink-0">
                <img
                  src={cat.imageUrl}
                  alt={`${cat.name} Vigorbuy Spreadsheet`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  title={`${cat.name} - Vigorbuy Spreadsheet`}
                />
              </div>
              <span className="text-xs text-center text-foreground-700 font-medium group-hover:text-primary-600 transition-colors leading-tight">
                {cat.name}
              </span>
            </a>
          ))}
        </div>

        <div className="relative max-w-md mx-auto" ref={dropdownRef}>
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="w-full flex items-center justify-between px-5 py-3.5 rounded-lg bg-background-100 border border-background-200 text-foreground-800 text-sm font-medium hover:border-primary-300 transition-all duration-200 cursor-pointer"
          >
            <span className="flex items-center gap-2">
              <i className="ri-grid-fill text-primary-500"></i>
              {isOpen ? 'Select a Category' : 'Choose a Product Category'}
            </span>
            <i className={`ri-arrow-down-s-line transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}></i>
          </button>

          {isOpen && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-background-50 border border-background-200 rounded-lg shadow-lg z-40 max-h-80 overflow-y-auto">
              {categories.map((cat, index) => (
                <a
                  key={index}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-3 px-5 py-3 text-sm text-foreground-800 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer first:rounded-t-lg last:rounded-b-lg"
                  onClick={() => setIsOpen(false)}
                >
                  <div className="w-8 h-8 rounded-md overflow-hidden flex-shrink-0">
                    <img
                      src={cat.imageUrl}
                      alt={cat.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <span>{cat.name}</span>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}